import { ccfRoutes } from '@app/ccf/ccf-routes';
import { ReportSection } from '@app/components/reports/types';
import { scrollTo } from '@app/components/reports/utils';
import { useGetCompanyReport } from '@shared/api/hooks/ccf/reports';
import { generatePath } from 'react-router-dom';
import { reportSlugs } from '../slugs';
import { companyReportUrls } from './company-report-utils';
import Appendix from './pages/appendix/appendix';
import { ElectricityCalculationAppendix } from './pages/appendix/electricity-calculation-appendix';
import { EmissionsQuantification } from './pages/appendix/emissions-quantification';
import MethodologyOverview from './pages/appendix/methodology-overview';
import OutOfScopeEmissionsAppendix from './pages/appendix/out-of-scope-emissions-appendix';
import Scope1CoverageAppendix from './pages/appendix/scope-1-coverage-appendix';
import { Scope1MethodologyAppendix } from './pages/appendix/scope-1-methodology-appendix';
import Scope2CoverageAppendix from './pages/appendix/scope-2-coverage-appendix';
import { Scope2MethodologyAppendix } from './pages/appendix/scope-2-methodology';
import Scope3CoverageAppendix from './pages/appendix/scope-3-coverage-appendix';
import Scope3MethodologyAppendix1 from './pages/appendix/scope-3-methodology-appendix-I';
import Scope3MethodologyAppendix2 from './pages/appendix/scope-3-methodology-appendix-II';
import AboutYourReport from './pages/introduction/about-your-report';
import CalculatingEmissions from './pages/introduction/calculating-emissions';
import Introduction from './pages/introduction/introduction';
import ResultsSummary from './pages/introduction/results-summary';
import YourFullCalculation from './pages/introduction/your-full-calculation';
import DirectGasEmissions from './pages/scope1/direct-gas-emissions';
import MobileCombustion from './pages/scope1/mobile-combustion';
import Scope1 from './pages/scope1/scope-1';
import Scope1BySite from './pages/scope1/scope-1-by-site';
import StationaryCombustion from './pages/scope1/stationary-combustion';
import Electricity from './pages/scope2/electricity';
import Scope2 from './pages/scope2/scope-2';
import Scope2BySite from './pages/scope2/scope-2-by-site';
import DistributionOfSoldProducts from './pages/scope3/distribution-of-sold-products';
import FoodAndDrink from './pages/scope3/food-and-drink';
import FoodAndDrinkItems from './pages/scope3/food-and-drink-items';
import FuelAndEnergy from './pages/scope3/fuel-and-energy';
import Packaging from './pages/scope3/packaging';
import PackagingItems from './pages/scope3/packaging-items';
import Scope3 from './pages/scope3/scope-3';
import SupplyChainLogistics from './pages/scope3/supply-chain-logistics';
import SupplyOfPurchasedGoods from './pages/scope3/supply-of-purchased-goods';

export function useCompanyReportSections(reportUuid?: string): ReportSection[] {
  const { data: reportDetail } = useGetCompanyReport(reportUuid ?? '');
  const periodParam = reportDetail?.timePeriod
    ? `?period=${reportDetail.timePeriod}`
    : '';
  if (!reportUuid) return [];
  return [
    {
      title: 'Introduction',
      url: companyReportUrls.introduction,
      preTitle: '',
      onClick: () => scrollTo(companyReportUrls.introduction),
      component: <Introduction />,
      show: true,
      items: [
        {
          title: 'Calculating emissions',
          preTitle: 'Introduction',
          component: <CalculatingEmissions />,
          url: companyReportUrls.calculatingEmissions,
          onClick: () => scrollTo(companyReportUrls.calculatingEmissions),
          show: true,
        },
        {
          title: 'About your report',
          preTitle: 'Introduction',
          url: companyReportUrls.aboutYourReport,
          onClick: () => scrollTo(companyReportUrls.aboutYourReport),
          show: true,
          component: <AboutYourReport />,
          link: {
            title: 'View methodology appendix',
            scrollTo: companyReportUrls.methodologyOverview,
          },
        },
      ],
    },
    {
      title: 'Results overview',
      url: companyReportUrls.resultsOverview,
      preTitle: '',
      onClick: () => scrollTo(companyReportUrls.resultsOverview),
      component: <ResultsSummary />,
      show: true,
      items: [
        {
          title: 'Full calculation',
          url: companyReportUrls.fullCalculation,
          onClick: () => scrollTo(companyReportUrls.fullCalculation),
          preTitle: 'Results overview',
          component: <YourFullCalculation />,
          show: true,
        },
      ],
    },
    {
      title: 'Scope 1',
      url: companyReportUrls.scope1,
      onClick: () => scrollTo(companyReportUrls.scope1),
      component: <Scope1 />,
      preTitle: '',
      link: {
        title: 'View scope 1 appendix',
        scrollTo: companyReportUrls.scope1Coverage,
      },
      show: true,
      items: [
        {
          title: 'Scope 1 by site',
          preTitle: 'Scope 1',
          slug: reportSlugs.scope1BySite,
          component: <Scope1BySite />,
          url: companyReportUrls.scope1BySite,
          onClick: () => scrollTo(companyReportUrls.scope1BySite),
          show: true,
        },
        {
          title: 'Stationary combustion',
          preTitle: 'Scope 1',
          slug: reportSlugs.stationaryCombustion,
          component: <StationaryCombustion />,
          show: true,
          url: companyReportUrls.stationaryCombustion,
          onClick: () => scrollTo(companyReportUrls.stationaryCombustion),
          link: {
            title: 'View input data',
            scrollTo: companyReportUrls.scope1Coverage,
            linkTo: generatePath(ccfRoutes.STATIONARY_COMBUSTION, {
              report_uuid: reportUuid,
            }),
          },
        },
        {
          title: 'Mobile combustion',
          url: companyReportUrls.mobileCombustion,
          slug: reportSlugs.mobileCombustion,
          onClick: () => scrollTo(companyReportUrls.mobileCombustion),
          component: <MobileCombustion />,
          preTitle: 'Scope 1',
          show: true,
          link: {
            title: 'View input data',
            linkTo: generatePath(ccfRoutes.MOBILE_COMBUSTION, {
              report_uuid: reportUuid,
            }),
          },
        },
        {
          title: 'Direct gas emissions',
          url: companyReportUrls.directGasEmissions,
          slug: reportSlugs.directGasEmissions,
          onClick: () => scrollTo(companyReportUrls.directGasEmissions),
          preTitle: 'Scope 1',
          show: true,
          component: <DirectGasEmissions />,
          link: {
            title: 'View input data',
            scrollTo: companyReportUrls.scope1Coverage,
            linkTo: generatePath(ccfRoutes.DIRECT_GAS, {
              report_uuid: reportUuid,
            }),
          },
        },
      ],
    },
    {
      title: 'Scope 2',
      url: companyReportUrls.scope2,
      onClick: () => scrollTo(companyReportUrls.scope2),
      preTitle: '',
      component: <Scope2 />,
      link: {
        title: 'View scope 2 appendix',
        scrollTo: companyReportUrls.scope2Coverage,
      },
      show: true,
      items: [
        {
          title: 'Scope 2 by site',
          url: companyReportUrls.scope2BySite,
          slug: reportSlugs.scope2BySite,
          onClick: () => scrollTo(companyReportUrls.scope2BySite),
          preTitle: 'Scope 2',
          component: <Scope2BySite />,
          show: true,
        },
        {
          title: 'Electricity',
          url: companyReportUrls.electricity,
          slug: reportSlugs.electricity,
          onClick: () => scrollTo(companyReportUrls.electricity),
          preTitle: 'Scope 2',
          component: <Electricity />,
          link: {
            title: 'View input data',
            scrollTo: companyReportUrls.scope1Coverage,
            linkTo: generatePath(ccfRoutes.ELECTRICITY, {
              report_uuid: reportUuid,
            }),
          },
          show: true,
        },
      ],
    },
    {
      title: 'Scope 3',
      url: companyReportUrls.scope3,
      onClick: () => scrollTo(companyReportUrls.scope3),
      preTitle: '',
      component: <Scope3 />,
      link: {
        title: 'View scope 3 appendix',
        scrollTo: companyReportUrls.scope3Coverage,
      },
      show: true,
      items: [
        {
          title: 'Food and drink',
          url: companyReportUrls.foodAndDrink,
          slug: reportSlugs.foodAndDrink,
          onClick: () => scrollTo(companyReportUrls.foodAndDrink),
          preTitle: 'Scope 3: Purchased goods and services',
          component: <FoodAndDrink />,
          link: {
            title: 'View input data',
            scrollTo: companyReportUrls.scope1Coverage,
            linkTo:
              generatePath(ccfRoutes.FOOD, {
                report_uuid: reportUuid,
              }) + periodParam,
          },
          show: true,
        },
        {
          title: 'Food and drink: items',
          url: companyReportUrls.foodAndDrinkItems,
          slug: reportSlugs.foodAndDrink,
          onClick: () => scrollTo(companyReportUrls.foodAndDrinkItems),
          preTitle: 'Scope 3: Purchased goods and services',
          component: <FoodAndDrinkItems />,
          show: true,
          link: {
            title: 'View input data',
            scrollTo: companyReportUrls.scope1Coverage,
            linkTo:
              generatePath(ccfRoutes.FOOD, {
                report_uuid: reportUuid,
              }) + periodParam,
          },
        },
        {
          title: 'Packaging',
          url: companyReportUrls.packaging,
          slug: reportSlugs.packaging,
          onClick: () => scrollTo(companyReportUrls.packaging),
          preTitle: 'Scope 3: Purchased goods and services',
          component: <Packaging />,
          show: true,
          link: {
            title: 'View input data',
            scrollTo: companyReportUrls.scope1Coverage,
            linkTo:
              generatePath(ccfRoutes.PACKAGING, {
                report_uuid: reportUuid,
              }) + periodParam,
          },
        },
        {
          title: 'Packaging: items',
          url: companyReportUrls.packagingItems,
          slug: reportSlugs.packaging,
          onClick: () => scrollTo(companyReportUrls.packagingItems),
          preTitle: 'Scope 3: Purchased goods and services',
          component: <PackagingItems />,
          show: true,
          link: {
            title: 'View input data',
            scrollTo: companyReportUrls.scope1Coverage,
            linkTo:
              generatePath(ccfRoutes.PACKAGING, {
                report_uuid: reportUuid,
              }) + periodParam,
          },
        },
        {
          title: 'Fuel and energy',
          url: companyReportUrls.fuelAndEnergy,
          slug: reportSlugs.fuelAndEnergy,
          onClick: () => scrollTo(companyReportUrls.fuelAndEnergy),
          preTitle: 'Scope 3',
          component: <FuelAndEnergy />,
          show: true,
          link: {
            title: 'View scope 3 appendix',
            scrollTo: companyReportUrls.scope3Coverage,
          },
        },
        {
          title: 'Supply chain logistics',
          preTitle: 'Scope 3: Transportation and distribution',
          url: companyReportUrls.supplyChainLogistics,
          slug: 'upstream-transportation-and-distribution',
          onClick: () => scrollTo(companyReportUrls.supplyChainLogistics),
          component: <SupplyChainLogistics />,
          show: true,
          link: {
            title: 'View appendix',
            scrollTo: companyReportUrls.scope3Coverage,
          },
        },
        {
          title: 'Supply of purchased goods',
          preTitle: 'Scope 3: Transportation and distribution',
          url: companyReportUrls.supplyOfPurchasedGoods,
          slug: 'upstream-transportation-and-distribution',
          onClick: () => scrollTo(companyReportUrls.supplyOfPurchasedGoods),

          component: <SupplyOfPurchasedGoods />,
          show: true,
          link: {
            title: 'View input data',
            scrollTo: companyReportUrls.scope3Coverage,
            linkTo: generatePath(ccfRoutes.SUPPLY_CHAIN_LOGISTICS, {
              report_uuid: reportUuid,
            }),
          },
        },
        {
          title: 'Distribution of sold products',
          url: companyReportUrls.distributionOfSoldProducts,
          slug: 'downstream-transportation-and-distribution',
          onClick: () => scrollTo(companyReportUrls.distributionOfSoldProducts),
          preTitle: 'Scope 3: Transportation and distribution',
          component: <DistributionOfSoldProducts />,
          show: true,
          link: {
            title: 'View input data',
            scrollTo: companyReportUrls.scope3Coverage,
            linkTo: generatePath(ccfRoutes.SUPPLY_CHAIN_LOGISTICS, {
              report_uuid: reportUuid,
            }),
          },
        },
      ],
    },
    {
      title: 'Appendix',
      url: companyReportUrls.appendix,
      onClick: () => scrollTo(companyReportUrls.appendix),
      preTitle: '',
      component: <Appendix />,
      show: true,
      items: [
        {
          title: 'Methodology overview',
          url: companyReportUrls.methodologyOverview,
          onClick: () => scrollTo(companyReportUrls.methodologyOverview),
          preTitle: 'Appendix',
          component: <MethodologyOverview />,
          show: true,
        },
        {
          title: 'Emissions quantification',
          url: companyReportUrls.emissionsQuantification,
          onClick: () => scrollTo(companyReportUrls.emissionsQuantification),
          preTitle: 'Appendix',
          component: <EmissionsQuantification />,
          show: true,
        },
        {
          title: 'Scope 1 coverage',
          url: companyReportUrls.scope1Coverage,
          onClick: () => scrollTo(companyReportUrls.scope1Coverage),
          preTitle: 'Appendix',
          component: <Scope1CoverageAppendix />,
          show: true,
        },
        {
          title: 'Scope 1 methodology',
          url: companyReportUrls.scope1Methodology,
          onClick: () => scrollTo(companyReportUrls.scope1Methodology),
          preTitle: 'Appendix',
          component: <Scope1MethodologyAppendix />,
          show: true,
        },
        {
          title: 'Scope 2 coverage',
          url: companyReportUrls.scope2Coverage,
          onClick: () => scrollTo(companyReportUrls.scope2Coverage),
          preTitle: 'Appendix',
          component: <Scope2CoverageAppendix />,
          show: true,
        },
        {
          title: 'Scope 2 methodology',
          onClick: () => scrollTo(companyReportUrls.scope2Methodology),
          url: companyReportUrls.scope2Methodology,
          preTitle: 'Appendix',
          component: <Scope2MethodologyAppendix />,
          show: true,
        },
        {
          title: 'Electricity calculation',
          url: companyReportUrls.electricityCalculation,
          onClick: () => scrollTo(companyReportUrls.electricityCalculation),

          preTitle: 'Appendix',
          component: <ElectricityCalculationAppendix />,
          show: true,
        },
        {
          title: 'Scope 3 coverage',
          url: companyReportUrls.scope3Coverage,
          onClick: () => scrollTo(companyReportUrls.scope3Coverage),
          preTitle: 'Appendix',
          component: <Scope3CoverageAppendix />,
          show: true,
        },
        {
          title: 'Scope 3 methodology - I',
          url: companyReportUrls.scope3Methodology1,
          onClick: () => scrollTo(companyReportUrls.scope3Methodology1),
          preTitle: 'Appendix',
          component: <Scope3MethodologyAppendix1 />,
          show: true,
        },
        {
          title: 'Scope 3 methodology - II',
          url: companyReportUrls.scope3Methodology2,
          onClick: () => scrollTo(companyReportUrls.scope3Methodology2),
          preTitle: 'Appendix',
          component: <Scope3MethodologyAppendix2 />,
          show: true,
        },
        {
          title: 'Out-of-scope emissions',
          url: companyReportUrls.outOfScopeEmissions,
          onClick: () => scrollTo(companyReportUrls.outOfScopeEmissions),
          preTitle: 'Appendix',
          component: <OutOfScopeEmissionsAppendix />,
          show: true,
        },
      ],
    },
  ];
}
